import { RiInformationLine, RiRobot2Line } from '@remixicon/react';
import classNames from 'classnames';

import { Flex, IconX, Text } from '@spotify-internal/encore-web';
import {
  ResourceNameLink,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import {
  ChatEventNotificationFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';

import { useSubagentPanel } from '../ChatThread/SubagentPanelContext';
import { MarkdownContent } from '../MarkdownContent';

import styles from './NotificationMessage.module.css';

type ChildChatStoppedMessageProps = {
  stopped: NonNullable<ChatEventNotificationFragment['childChatStopped']>;
};

const ChildChatStoppedMessage = ({ stopped }: ChildChatStoppedMessageProps) => {
  const panel = useSubagentPanel();
  const childChat = getTypeOrNull(stopped.childChat);
  const success = stopped.success ?? true;

  const childTitle = childChat?.displayName ?? 'Subagent';
  const statusLabel = success ? 'finished' : 'failed';

  const label = (
    <Text variant="bodySmall" semanticColor="textSubdued">
      {childChat && panel ? (
        <span
          className={styles.link}
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation();
            panel.openSubagentPanel(childChat.name);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              panel.openSubagentPanel(childChat.name);
            }
          }}
        >
          {childTitle}
        </span>
      ) : (
        childTitle
      )}
      {` ${statusLabel}`}
    </Text>
  );

  return (
    <div className={styles.root}>
      <div className={styles.headerStatic}>
        <RiRobot2Line size={14} className={styles.icon} />
        {!success && <IconX size="xsmall" className={styles.failureIcon} />}
        {label}
      </div>
    </div>
  );
};

type SystemMessageProps = {
  message: NonNullable<ChatEventNotificationFragment['systemMessage']>;
};

const SystemMessage = ({ message }: SystemMessageProps) => {
  if (!message.displayText) return null;
  return (
    <div className={styles.root}>
      <div className={classNames(styles.headerStatic, styles.alignStart)}>
        <RiInformationLine size={14} className={styles.icon} />
        <Flex direction="column" gap="tighter-4">
          <MarkdownContent size="small" className={styles.systemText}>
            {message.displayText}
          </MarkdownContent>
          {message.resources.length > 0 && (
            <Flex wrap="wrap" gap="tighter-2">
              {message.resources.map(resource => (
                <Text
                  key={resource}
                  variant="bodySmall"
                  semanticColor="textSubdued"
                >
                  <ResourceNameLink
                    name={resource}
                    text={extractLastNameComponent(resource) ?? resource}
                  />
                </Text>
              ))}
            </Flex>
          )}
        </Flex>
      </div>
    </div>
  );
};

type NotificationMessageProps = {
  notification: ChatEventNotificationFragment;
};

export const NotificationMessage = ({
  notification,
}: NotificationMessageProps) => {
  if (notification.childChatStopped) {
    return <ChildChatStoppedMessage stopped={notification.childChatStopped} />;
  }
  if (notification.systemMessage) {
    return <SystemMessage message={notification.systemMessage} />;
  }
  return null;
};
